/**
 * Whole-exam screening workbench (Epic C1) — database-backed, non-diagnostic.
 */

import React from "react";
import { Alert, Badge, Button, Card, Col, Container, Row, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { usePortalWorkbenchSnapshot } from "../services/portalWorkbenchApi";

export default function StudyScreeningPanel() {
  const { snapshot, loading, error, reload } = usePortalWorkbenchSnapshot();
  const labeling = snapshot?.studyScreening.labeling;
  const candidates = snapshot?.studyScreening.candidates ?? [];

  return (
    <Container fluid className="py-3" data-testid="study-screening-panel">
      <Alert variant="warning" className="d-flex justify-content-between gap-3 align-items-start">
        <div>
          <strong>{labeling?.badge ?? "Assistive screening"}</strong>
          <div className="small mt-1">
            {labeling?.intendedUse ??
              "Prioritizes whole-exam review from database evidence. Clinicians independently review raw EEG before interpretation."}
          </div>
        </div>
        <Button size="sm" variant="outline-dark" onClick={reload} disabled={loading}>
          Refresh
        </Button>
      </Alert>
      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Row className="g-3">
        <Col lg={9}>
          <Card>
            <Card.Header>Whole-exam review queue</Card.Header>
            <Table size="sm" responsive className="mb-0" data-testid="screening-candidates">
              <thead>
                <tr>
                  <th>Patient / study</th>
                  <th>Priority</th>
                  <th>Screen</th>
                  <th>Evidence basis</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5}>Loading database screening queue...</td></tr>
                ) : candidates.length === 0 ? (
                  <tr><td colSpan={5}>No studies are currently available for screening.</td></tr>
                ) : (
                  candidates.map((candidate) => (
                    <tr key={candidate.studyId}>
                      <td>
                        <div className="fw-semibold">{candidate.patientLabel}</div>
                        <div className="small text-muted">Study {candidate.studyId}</div>
                      </td>
                      <td><Badge bg={candidate.priority === "stat" ? "danger" : "secondary"}>{candidate.priority}</Badge></td>
                      <td>
                        {candidate.label}
                        {candidate.confidencePercent != null ? (
                          <span className="text-muted"> · {candidate.confidencePercent}%</span>
                        ) : null}
                      </td>
                      <td className="small">{candidate.rationale.join(" · ")}</td>
                      <td>
                        <Button as={Link as any} size="sm" variant="outline-primary" to={`/eeg/${candidate.studyId}`}>
                          Review
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card>
        </Col>
        <Col lg={3}>
          <Card className="mb-3">
            <Card.Header>Database coverage</Card.Header>
            <Card.Body data-testid="screening-badge">
              <div className="d-flex justify-content-between">
                <span>Studies</span>
                <strong>{snapshot?.summary.studies ?? 0}</strong>
              </div>
              <div className="d-flex justify-content-between">
                <span>Recordings</span>
                <strong>{snapshot?.summary.recordings ?? 0}</strong>
              </div>
              <div className="d-flex justify-content-between">
                <span>Spikes/events</span>
                <strong>{snapshot?.summary.spikes ?? 0}</strong>
              </div>
            </Card.Body>
          </Card>
          <Card>
            <Card.Header>Excluded use</Card.Header>
            <Card.Body className="small text-muted">
              {labeling?.excludedUse ??
                "Not for autonomous diagnosis, closed-loop therapy, or production diagnostic badges outside the approved phase gate."}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
